import { HttpError, useOne } from "@refinedev/core";
import { DatePickerInput } from "components/datePickerInput";
import Select from "components/select";
import { DatePickerFilterOption, IGatewayFilterOptions, IOrgEventIdsItem, IOrgIdsItem } from "interfaces";
import moment from "moment";
import { useMemo, useState } from "react";
import { Addons, Groups, ParticipantsInfo, PendingPayment, Tickets, TShirtSizeInfo } from "./components";
import { isEmpty } from "lodash";
import { GATEWAY_OWNER_OPTIONS, TICKET_PLATFORMS } from "utills/constants";

enum Tabs {
  TICKETS = 0,
  GROUPS = 1,
}

export const Event: React.FC = () => {
  const [dateOption, setDateOption] = useState(DatePickerFilterOption.YEAR);
  const [filters, setFilters] = useState({
    organization_id: "all",
    event_id: "",
    from_date: "",
    ticket_platform: "",
    to_date: "",
    gateway_owner: "all",
    gateway_partner_code: "",
    gateway_name: ""
  });
  const [selectedTabIndex, setSelectedTabIndex] = useState(Tabs.TICKETS);

  const { data: orgIds, isLoading: orgIdsLoading } = useOne<IOrgIdsItem[], HttpError>({
    resource: "dashboard/organization-filter-options",
    id: "",
    meta: {
      resource: "data",
      filters: [
        { field: "gateway_owner", operator: "eq", value: filters.gateway_owner },
        { field: "gateway_partner_code", operator: "eq", value: filters.gateway_partner_code },
        { field: "gateway_name", operator: "eq", value: filters.gateway_name },
        { field: "ticket_platform", operator: "eq", value: filters.ticket_platform },
      ]
    }
  });

  const organizationIds = (([{ label: "All", value: "all" }] as {label: string; value: string}[])).concat(
    (orgIds?.data ?? []).map((org: IOrgIdsItem) => ({ value: org.id, label: org.name_en }))
  );

  const { data: organizationEventsData, isLoading: isLoadingEvents } = useOne<IOrgEventIdsItem[], HttpError>({
    resource: "organization",
    id: `${filters.organization_id !== "all" ? filters.organization_id : "all"}/events/dashboard`,
    meta: {
      resource: "data",
    },
  });

  const organizationEventIds = useMemo(() =>
    (organizationEventsData?.data ?? []).map((event: IOrgEventIdsItem) => (
      { value: event.id, label: event.name_en, type: event.event_type, currency: event.currency, organization_id: event.organization_id }
    )),
    [organizationEventsData?.data]
  );

  const sharedFilterValues = useMemo(() => ({
    event_id: filters.event_id || organizationEventIds[0]?.value || "",
    from_date: filters.from_date,
    to_date: filters.to_date,
    ticket_platform: filters.ticket_platform,
    gateway_owner: filters.gateway_owner,
    gateway_partner_code: filters.gateway_partner_code,
    gateway_name: filters.gateway_name,
  }), [filters, organizationEventIds]);

  const selectedEventOrganizationId = organizationEventIds.find(
    (e: { value: string }) => e.value === (filters.event_id || organizationEventIds[0]?.value)
  )?.organization_id || "";

  const { data: gatewayFilterOptions } = useOne<IGatewayFilterOptions, HttpError>({
    resource: "dashboard/gateway-filter-options",
    id: "",
    meta: {
      resource: "data",
      filters: [
        { field: "gateway_owner", operator: "eq", value: filters.gateway_owner },
        { field: "organization_id", operator: "eq", value: selectedEventOrganizationId },
        { field: "ticket_platform", operator: "eq", value: filters.ticket_platform },
      ]
    },
    queryOptions: {
      enabled: !isLoadingEvents,
    } as any,
  });

  const partnerCodeOptions = ([{ label: "All", value: "" }]).concat(
    (gatewayFilterOptions?.data?.partner_codes ?? []).map((code: string) => ({ label: code, value: code }))
  );

  const gatewayNameOptions = ([{ label: "All", value: "" }]).concat(
    (gatewayFilterOptions?.data?.gateway_names ?? []).map((name: string) => ({ label: name, value: name }))
  );

  const ticketPlatformList = ([{label: "All", value: ""}]).concat(TICKET_PLATFORMS ?? []);

  const _handleDateChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setFilters({
      ...filters,
      from_date: moment(start).format("YYYY-MM-DD"),
      to_date: moment(end).format("YYYY-MM-DD")
    })
  }

  const selectedEventType = organizationEventIds.find((selected: {label: string; value: string}) => selected.value === (filters.event_id || organizationEventIds[0].value))?.type;
  const selectedEventCurrency = organizationEventIds.find((selected: {value: string}) => selected.value === filters.event_id)?.currency;
  
  return (
    <div className="wrapper">
      <div className="box-container">
        <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-y-4 gap-x-2 md:gap-x-4">
          <div className="flex-1 shrink-0">
            <label htmlFor="gateway_owner" className="block text-sm font-medium leading-6">
              Gateway Owner
            </label>
            <div className="mt-2">
              <Select 
                key="gateway_owner"
                options={GATEWAY_OWNER_OPTIONS}
                id="gateway_owner"
                placeholder="Select Gateway Owner"
                className="py-0.5 w-full sm:max-w-full !rounded-md"
                value={GATEWAY_OWNER_OPTIONS.find((selected) => selected.value === filters.gateway_owner)}
                onChange={(selected: {label: string; value: string}) => {
                  setFilters({ ...filters, gateway_owner: selected.value, ticket_platform: "", gateway_partner_code: "", gateway_name: "" });
                }}      
              />
            </div>
          </div>
          <div className="flex-1 shrink-0">
            <label htmlFor="organization_id" className="block text-sm font-medium leading-6">
              Organization
            </label>
            <div className="mt-2">
              <Select
                key={`organization_id__${filters.gateway_owner}__${filters.gateway_partner_code}__${filters.gateway_name}`}
                options={organizationIds}
                id="organization_id"
                isLoading={orgIdsLoading}
                placeholder="Select Organization"
                className="py-0.5 w-full sm:max-w-full !rounded-md"
                value={organizationIds.find((selected) => selected.value === filters.organization_id)}
                onChange={(selected: {label: string; value: string}) => {
                  setFilters({ ...filters, organization_id: selected.value, event_id: "" });
                }}
              />
            </div>
          </div>
          <div className="flex-1 shrink-0">
            <label htmlFor="gateway_partner_code" className="block text-sm font-medium leading-6">
              Gateway Partner Code
            </label>
            <div className="mt-2">
              <Select 
                key="gateway_partner_code"
                options={partnerCodeOptions}
                id="gateway_partner_code"
                placeholder="Select Partner Code"
                className="py-0.5 w-full sm:max-w-full !rounded-md"
                value={partnerCodeOptions.find((selected) => selected.value === filters.gateway_partner_code)}
                onChange={(selected: {label: string; value: string}) => {
                  setFilters({ ...filters, gateway_partner_code: selected.value });
                }}      
              />
            </div>
          </div>
          <div className="flex-1 shrink-0">
            <label htmlFor="gateway_name" className="block text-sm font-medium leading-6">
              Gateway Name
            </label>
            <div className="mt-2">
              <Select 
                key="gateway_name"
                options={gatewayNameOptions}
                id="gateway_name"
                placeholder="Select Gateway Name"
                className="py-0.5 w-full sm:max-w-full !rounded-md"
                value={gatewayNameOptions.find((selected) => selected.value === filters.gateway_name)}
                onChange={(selected: {label: string; value: string}) => {
                  setFilters({ ...filters, gateway_name: selected.value });
                }}      
              />
            </div>
          </div>
          <div className="flex-1 shrink-0">
            <label htmlFor="event" className="block text-sm font-medium leading-6">
              Event Name
            </label>
            <div className="mt-2">
              <Select 
                key={`event-id`}
                options={organizationEventIds}
                id="event"
                isLoading={isLoadingEvents}
                placeholder="Select Event"
                className="py-0.5 w-full sm:max-w-full !rounded-md"
                value={organizationEventIds.find((selected: {label: string; value: string}) => selected.value === (filters.event_id || organizationEventIds[0].value))}
                onChange={(selected: {label: string; value: string, type: string}) => {
                    setFilters({
                    ...filters,
                    event_id: selected.value
                  });
                  if (selected.type === "attractions" && selectedTabIndex !== Tabs.TICKETS) { // select ticket tab if selected event type is sports
                    setSelectedTabIndex(Tabs.TICKETS)
                  }
                }}      
              />
            </div>
          </div>
          <div className="flex-1">
            <label htmlFor="date" className="block text-sm font-medium leading-6">
              Time
            </label>
            <div className="mt-2 w-full">
              <div className="w-full shrink-0 bg-white rounded-md">
                <DatePickerInput 
                  defaultOption={dateOption}
                  setDateOption={setDateOption}
                  defaultStartDate={filters.from_date ? moment(filters.from_date).toDate() : null} 
                  defaultEndDate={filters.to_date ?  moment(filters.to_date).toDate() : null}
                  _handleDateChange={_handleDateChange}
                />
              </div>
            </div>
          </div>
          {(filters.gateway_owner === "all" || filters.gateway_owner === "actiup") && (
            <div className="flex-1 shrink-0">
              <label htmlFor="ticket_platform" className="block text-sm font-medium leading-6">
                Platform
              </label>
              <div className="mt-2">
                <Select 
                  key="ticket_platform"
                  options={ticketPlatformList}
                  id="ticket_platform"
                  placeholder="Select Ticket Platform"
                  className="py-0.5 w-full sm:max-w-full !rounded-md"
                  value={ticketPlatformList.find((selected: {label: string; value: string}) => selected.value === (filters.ticket_platform))}
                  onChange={(selected: {label: string; value: string, type: string}) => {
                    setFilters({
                      ...filters,
                      ticket_platform: selected.value
                    });
                  }}      
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {!isEmpty(organizationEventIds) ? (
        <>
          <div className="box-container">
            <div className="border-b border-solid border-gray-100 mb-2 sm:mb-4">
              <div className="relative w-full flex items-center md:max-w-[280px]">
                <button className={`flex-1 pb-3 h-full ${selectedTabIndex === Tabs.TICKETS ? "text-brand-500 font-medium" : ""}`} onClick={() => setSelectedTabIndex(Tabs.TICKETS)}>Tickets</button>
                {selectedEventType !== "attractions" ? (
                  <button className={`flex-1 pb-3 ${selectedTabIndex === Tabs.GROUPS ? "text-brand-500 font-medium" : ""}`} onClick={() => setSelectedTabIndex(Tabs.GROUPS)}>Groups</button>
                ) : <div className="flex-1 pb-3"></div>}
                <div className="absolute bottom-0 left-0 w-[50%] h-1 bg-brand-400 rounded-t-xl transition-all" style={{marginLeft: `calc(50% * ${selectedTabIndex})`}}></div>
              </div>
            </div>
            <div style={{display: selectedTabIndex === Tabs.TICKETS ? "block" : "none"}}>
              <Tickets 
                key={Tabs.TICKETS}
                isTrigger={selectedTabIndex === Tabs.TICKETS}
                currentFilterValues={sharedFilterValues}
                type={selectedEventType}
                currency={selectedEventCurrency}
              />
            </div>
            {selectedEventType !== "attractions" ? (
              <div style={{display: selectedTabIndex === Tabs.GROUPS ? "block" : "none"}}>
                <Groups 
                  key={Tabs.GROUPS}
                  isTrigger={selectedTabIndex === Tabs.GROUPS}
                  currentFilterValues={sharedFilterValues}
                  currency={selectedEventCurrency}
                />
              </div>
            ) : <></>}
          </div>
          {selectedTabIndex !== Tabs.GROUPS ? (
            <>
              {(selectedEventType !== "attractions") ? (
                <div className="box-container p-0">
                  <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">T-SHIRT SIZE INFORMATION</h3>
                  </div>
                  <div>
                    <TShirtSizeInfo currentFilterValues={sharedFilterValues} />
                  </div>
                </div>
              ) : <></>}
              <div className="box-container p-0">
                <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">PARTICIPANTS INFORMATION</h3>
                </div>
                <div>
                  <ParticipantsInfo currentFilterValues={sharedFilterValues} />
                </div>
              </div>
              <div className="box-container p-0">
                <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">ADDONS</h3>
                </div>
                <div>
                  <Addons currentFilterValues={sharedFilterValues} currency={selectedEventCurrency} />
                </div>
              </div>
              <div className="box-container p-0">
                <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">PENDING PAYMENT</h3>
                </div>
                <div>
                  <PendingPayment currentFilterValues={sharedFilterValues} />
                </div>
              </div>
            </>
          ) : <></>}
        </>
      ) : <></>}
    </div>
  );
}
